import * as THREE from 'three';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import {
  DEFAULT_RENDERER_MAX_DPR,
  getCappedDevicePixelRatio,
  getRendererDiagnostics,
  resizeRenderer,
  type RendererDiagnostics,
} from '../../core/Renderer';

export interface BloomSettings {
  readonly enabled: boolean;
  readonly strength: number;
  readonly radius: number;
  readonly threshold: number;
}

export interface RenderPipelineOptions {
  /** Bypasses all composer passes when false. Defaults to true. */
  readonly postProcessing?: boolean;
  /** false disables bloom while retaining the composer output pass. */
  readonly bloom?: false | Partial<BloomSettings>;
  readonly maxDpr?: number;
}

export interface RenderPassMetadata {
  readonly id: 'scene' | 'bloom' | 'output';
  readonly enabled: boolean;
  readonly kind: 'scene-render' | 'unreal-bloom' | 'tone-map-output';
  readonly fullScreen: boolean;
  readonly notes: string;
}

export interface RenderPipelineMetadata {
  readonly version: 1;
  readonly colorPipeline: 'linear-working-space -> ACES filmic -> sRGB output';
  readonly maxDpr: number;
  readonly bloomLimits: Readonly<{
    maxStrength: number;
    maxRadius: number;
    minThreshold: number;
  }>;
  readonly passes: readonly RenderPassMetadata[];
}

export interface RenderPipelineDiagnostics {
  readonly postProcessingEnabled: boolean;
  readonly bloom: BloomSettings;
  readonly passes: readonly RenderPassMetadata[];
  readonly framesRendered: number;
  readonly composerFrames: number;
  readonly directFrames: number;
  readonly width: number;
  readonly height: number;
  readonly pixelRatio: number;
  readonly renderer: RendererDiagnostics;
}

const DEFAULT_BLOOM: BloomSettings = Object.freeze({
  enabled: true,
  strength: 0.18,
  radius: 0.2,
  threshold: 1.05,
});

const BLOOM_LIMITS = Object.freeze({
  maxStrength: 0.5,
  maxRadius: 0.5,
  minThreshold: 0.7,
});

/**
 * Integration-friendly scene render owner. The default bloom is deliberately
 * restrained and thresholded so bright authored emissives lift without
 * veiling coins, cards, rails, or collision silhouettes.
 */
export class RenderPipeline {
  readonly metadata: RenderPipelineMetadata;

  private readonly composer: EffectComposer;
  private readonly scenePass: RenderPass;
  private readonly bloomPass: UnrealBloomPass;
  private readonly outputPass: OutputPass;
  private readonly maxDprValue: number;
  private postProcessingEnabledValue: boolean;
  private disposed = false;
  private framesRendered = 0;
  private composerFrames = 0;
  private directFrames = 0;
  private lastWidth = 1;
  private lastHeight = 1;
  private lastPixelRatio = 1;

  constructor(
    readonly renderer: THREE.WebGLRenderer,
    readonly scene: THREE.Scene,
    readonly camera: THREE.Camera,
    options: RenderPipelineOptions = {},
  ) {
    this.maxDprValue = readPositiveFinite(
      options.maxDpr ?? DEFAULT_RENDERER_MAX_DPR,
      'maxDpr',
    );
    this.postProcessingEnabledValue = options.postProcessing ?? true;
    const bloom = normalizeBloomSettings(options.bloom);

    this.composer = new EffectComposer(renderer);
    this.scenePass = new RenderPass(scene, camera);
    this.bloomPass = new UnrealBloomPass(
      new THREE.Vector2(1, 1),
      bloom.strength,
      bloom.radius,
      bloom.threshold,
    );
    this.bloomPass.enabled = bloom.enabled;
    this.outputPass = new OutputPass();
    this.composer.addPass(this.scenePass);
    this.composer.addPass(this.bloomPass);
    this.composer.addPass(this.outputPass);

    this.metadata = Object.freeze({
      version: 1 as const,
      colorPipeline: 'linear-working-space -> ACES filmic -> sRGB output' as const,
      maxDpr: this.maxDprValue,
      bloomLimits: BLOOM_LIMITS,
      passes: this.createPassMetadata(),
    });
    this.resize();
  }

  get postProcessingEnabled(): boolean {
    return this.postProcessingEnabledValue;
  }

  get bloom(): BloomSettings {
    return Object.freeze({
      enabled: this.bloomPass.enabled,
      strength: this.bloomPass.strength,
      radius: this.bloomPass.radius,
      threshold: this.bloomPass.threshold,
    });
  }

  setPostProcessingEnabled(enabled: boolean): void {
    this.assertActive();
    this.postProcessingEnabledValue = enabled;
  }

  setBloomEnabled(enabled: boolean): void {
    this.assertActive();
    this.bloomPass.enabled = enabled;
  }

  setBloom(settings: Partial<Omit<BloomSettings, 'enabled'>>): BloomSettings {
    this.assertActive();
    if (settings.strength !== undefined) {
      this.bloomPass.strength = readRange(
        settings.strength,
        0,
        BLOOM_LIMITS.maxStrength,
        'bloom.strength',
      );
    }
    if (settings.radius !== undefined) {
      this.bloomPass.radius = readRange(
        settings.radius,
        0,
        BLOOM_LIMITS.maxRadius,
        'bloom.radius',
      );
    }
    if (settings.threshold !== undefined) {
      this.bloomPass.threshold = readRange(
        settings.threshold,
        BLOOM_LIMITS.minThreshold,
        4,
        'bloom.threshold',
      );
    }
    return this.bloom;
  }

  setExposure(exposure: number): void {
    this.assertActive();
    this.renderer.toneMappingExposure = readPositiveFinite(exposure, 'exposure');
  }

  /** Updates renderer, camera, composer targets, and DPR together. */
  resize(): boolean {
    this.assertActive();
    const canvas = this.renderer.domElement;
    const width = Math.max(1, Math.floor(canvas.clientWidth));
    const height = Math.max(1, Math.floor(canvas.clientHeight));
    const pixelRatio = getCappedDevicePixelRatio(this.maxDprValue);
    const cameraResized = this.camera instanceof THREE.PerspectiveCamera
      ? resizeRenderer(this.renderer, this.camera, this.maxDprValue)
      : resizeRendererWithoutPerspectiveCamera(
          this.renderer,
          width,
          height,
          pixelRatio,
        );
    const composerResized =
      width !== this.lastWidth ||
      height !== this.lastHeight ||
      pixelRatio !== this.lastPixelRatio;

    if (composerResized || cameraResized) {
      this.composer.setPixelRatio(pixelRatio);
      this.composer.setSize(width, height);
      this.lastWidth = width;
      this.lastHeight = height;
      this.lastPixelRatio = pixelRatio;
    }
    return composerResized || cameraResized;
  }

  render(deltaSeconds = 0): void {
    this.assertActive();
    this.framesRendered += 1;
    if (this.postProcessingEnabledValue) {
      this.composerFrames += 1;
      this.composer.render(clamp(deltaSeconds, 0, 0.1));
    } else {
      this.directFrames += 1;
      this.renderer.render(this.scene, this.camera);
    }
  }

  getDiagnostics(): RenderPipelineDiagnostics {
    return Object.freeze({
      postProcessingEnabled: this.postProcessingEnabledValue,
      bloom: this.bloom,
      passes: this.createPassMetadata(),
      framesRendered: this.framesRendered,
      composerFrames: this.composerFrames,
      directFrames: this.directFrames,
      width: this.lastWidth,
      height: this.lastHeight,
      pixelRatio: this.lastPixelRatio,
      renderer: getRendererDiagnostics(this.renderer),
    });
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.scenePass.dispose();
    this.bloomPass.dispose();
    this.outputPass.dispose();
    this.composer.dispose();
  }

  private createPassMetadata(): readonly RenderPassMetadata[] {
    return Object.freeze([
      Object.freeze({
        id: 'scene' as const,
        enabled: this.scenePass?.enabled ?? true,
        kind: 'scene-render' as const,
        fullScreen: false,
        notes: 'Opaque/transparent authored scene render.',
      }),
      Object.freeze({
        id: 'bloom' as const,
        enabled: this.bloomPass?.enabled ?? DEFAULT_BLOOM.enabled,
        kind: 'unreal-bloom' as const,
        fullScreen: true,
        notes: 'Restrained high-threshold mip bloom for emissive accents only.',
      }),
      Object.freeze({
        id: 'output' as const,
        enabled: this.outputPass?.enabled ?? true,
        kind: 'tone-map-output' as const,
        fullScreen: true,
        notes: 'ACES tone mapping and sRGB output conversion.',
      }),
    ]);
  }

  private assertActive(): void {
    if (this.disposed) throw new Error('RenderPipeline has been disposed.');
  }
}

export function createRenderPipeline(
  renderer: THREE.WebGLRenderer,
  scene: THREE.Scene,
  camera: THREE.Camera,
  options: RenderPipelineOptions = {},
): RenderPipeline {
  return new RenderPipeline(renderer, scene, camera, options);
}

function normalizeBloomSettings(
  options: false | Partial<BloomSettings> | undefined,
): BloomSettings {
  if (options === false) return Object.freeze({ ...DEFAULT_BLOOM, enabled: false });
  return Object.freeze({
    enabled: options?.enabled ?? DEFAULT_BLOOM.enabled,
    strength: readRange(
      options?.strength ?? DEFAULT_BLOOM.strength,
      0,
      BLOOM_LIMITS.maxStrength,
      'bloom.strength',
    ),
    radius: readRange(
      options?.radius ?? DEFAULT_BLOOM.radius,
      0,
      BLOOM_LIMITS.maxRadius,
      'bloom.radius',
    ),
    threshold: readRange(
      options?.threshold ?? DEFAULT_BLOOM.threshold,
      BLOOM_LIMITS.minThreshold,
      4,
      'bloom.threshold',
    ),
  });
}

function resizeRendererWithoutPerspectiveCamera(
  renderer: THREE.WebGLRenderer,
  width: number,
  height: number,
  pixelRatio: number,
): boolean {
  const canvas = renderer.domElement;
  const bufferWidth = Math.floor(width * pixelRatio);
  const bufferHeight = Math.floor(height * pixelRatio);
  const changed =
    canvas.width !== bufferWidth ||
    canvas.height !== bufferHeight ||
    renderer.getPixelRatio() !== pixelRatio;
  if (changed) {
    renderer.setPixelRatio(pixelRatio);
    renderer.setSize(width, height, false);
  }
  return changed;
}

function readPositiveFinite(value: number, label: string): number {
  if (!Number.isFinite(value) || value <= 0) {
    throw new RangeError(`${label} must be a finite number greater than zero.`);
  }
  return value;
}

function readRange(value: number, min: number, max: number, label: string): number {
  if (!Number.isFinite(value) || value < min || value > max) {
    throw new RangeError(`${label} must be a finite number from ${min} through ${max}.`);
  }
  return value;
}

function clamp(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.min(max, Math.max(min, value));
}
